import LFCBaseCharts from '_components/charts/base/LFCBaseCharts';
import {fmtYYYYMMDDHHmmss} from '_logics/LFCFormatUtil';
import {useParseWithSettings} from '_logics/LFCParseUtil';
import {colorPallet} from '_themes/color-pallet/ColorPallet';
import dayjs from 'dayjs';
import {
  CloudDistanceDigGraphBaseData,
  SearchType
} from 'pages/lq-job/cloud-distance/components/cloud-distance-dig/types';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {blue, lightBlue, pink} from '@mui/material/colors';

/**
 * 引数
 */
interface Props {
  title: string;
  source: CloudDistanceDigGraphBaseData;
  x?: {
    type: 'time' | 'value' | 'category';
    dsColumn: string;
  };
  y?: {
    type: 'time' | 'value' | 'category';
    dsColumn: string;
  };
  exportFilename?: string;
  height?: string;
  zoomChartId?: string;
  zoomedChartId?: string;
  zoomCharts?: (zoomChartId?: string) => void;
  zoomChartsClose?: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;
  averageUpperDistance?: any;
  medianUpperDistance?: any;
  averagePlus3SD?: any;
  averageMinus3SD?: any;
  searchType?: number;
}

/**
 * LFCChartsCloudDistanceMinTrend
 * @param props
 * @returns
 */
const LFCChartsCloudDistanceMinTrend = React.memo(
  ({
    title,
    source,
    exportFilename,
    height,
    zoomChartId,
    zoomedChartId,
    zoomCharts,
    zoomChartsClose,
    averageUpperDistance,
    medianUpperDistance,
    averagePlus3SD,
    averageMinus3SD,
    searchType
  }: Props) => {
    const {t} = useTranslation();
    const {parseSize} = useParseWithSettings();
    const [boxData, setBoxData] = useState([]);
    const [legendSelected, setLegendSelected] = useState<any>({});
    const [tooltipContent, setTooltipContent] = useState('');

    const CalcQ = (data: number[], coefficient: number): number => {
      const data_s = data.sort((a: number, b: number) => a - b);
      const pos = (data_s.length - 1) * coefficient;
      const base = Math.floor(pos);
      const rest = pos - base;
      if (data_s[base + 1] !== undefined) {
        return data_s[base] + rest * (data_s[base + 1] - data_s[base]);
      } else {
        return data_s[base];
      }
    };

    const CalcQuartile = (data: number[]): Array<number> => {
      if (data.length === 0) {
        return [];
      }
      const min = Math.min(...data.map((a: any) => Number(a)));
      const q1 = CalcQ(data, 0.25);
      const q2 = CalcQ(data, 0.5);
      const q3 = CalcQ(data, 0.75);
      const max = Math.max(...data.map((a: any) => Number(a)));

      // [min,  Q1,  median (or Q2),  Q3,  max]
      return [min, q1, q2, q3, max];
    };
    const [exportFields, setExportFields] = useState({});
    const [exportData, setExportData] = useState<any>([]);

    useEffect(() => {
      //ボックスデータの算出
      let tmpOutPut: any = [];
      tmpOutPut[0] = [];
      tmpOutPut[1] = [];
      tmpOutPut[2] = [];

      if (searchType === SearchType.WORK) {
        setLegendSelected({
          最小値: true,
          上限: true,
          下限: true
        });
      } else {
        setLegendSelected({
          箱ひげ図: true,
          群平均: false,
          群中央: false,
          上限: true,
          下限: true,
          中央: false,
          平均: false,
          '平均+3σ': false,
          '平均-3σ': false
        });
        source.xlist.forEach((b: string, index: number) => {
          tmpOutPut[0].push(CalcQuartile(source.data_upper_distance[index] as number[]));
          tmpOutPut[1].push(CalcQuartile(source.data_lower_distance[index] as number[]));
        });
      }
      setBoxData(tmpOutPut);
    }, [source]);

    useEffect(() => {
      if (boxData.length === 0) return;

      if (searchType === SearchType.WORK) {
        //検査ごと
        setExportFields({
          シリアルナンバー: t('シリアルナンバー'),
          検査日時: t('検査日時'),
          検査部位: t('検査部位'),
          最小値: t('最小値'),
          上限: t('上限'),
          下限: t('下限')
        });

        const _exportData = source?.xlist.map((xval: string, i: number) => {
          return {
            シリアルナンバー: source.detail[i].serial || source.detail[i].uuid,
            検査日時: fmtYYYYMMDDHHmmss(source.detail[i].lq_time),
            検査部位: source.map,
            最小値: parseSize(source.data_lower_distance[i] as number),
            上限: parseSize(source.data_upper_limits[i] as number),
            下限: parseSize(source.data_lower_limits[i] as number)
          };
        });
        setExportData(_exportData);
      } else {
        setExportFields({
          検査日時: t('検査日時'),
          検査部位: t('検査部位'),
          最小値_最大: t('最小値　最大'),
          最小値_Q3: t('最小値　Q3'),
          最小値_中央: t('最小値　中央'),
          最小値_Q1: t('最小値　Q1'),
          最小値_最小: t('最小値　最小'),
          上限: t('上限'),
          下限: t('下限')
        });

        const _exportData = source?.xlist
          .map((xval: string, i: number) => {
            if (!Array.isArray(boxData[0][i])) return null;
            if ((boxData[1][i] as any).length === 0) return null;

            return {
              検査日時: fmtYYYYMMDDHHmmss(xval),
              検査部位: source.map,
              最小値_最大: parseSize(boxData[1][i][4]),
              最小値_Q3: parseSize(boxData[1][i][3]),
              最小値_中央: parseSize(boxData[1][i][2]),
              最小値_Q1: parseSize(boxData[1][i][1]),
              最小値_最小: parseSize(boxData[1][i][0]),
              上限: parseSize(source.limits_max),
              下限: parseSize(source.limits_min)
            };
          })
          .filter((item: any) => item !== null); // nullの行を除外

        setExportData(_exportData);
      }
    }, [boxData]);

    const calcDataAvgMel = (source: any) => {
      return source.data_lower_distance.map((sublist: any[]) => {
        if (!Array.isArray(sublist) || sublist.length === 0) {
          return null;
        }
        const sum = sublist.reduce((acc, val) => acc + val, 0);
        return sum / sublist.length;
      });
    };
    const calcDataMiddleMel = (source: any) => {
      return source.data_lower_distance.map((sublist: any[]) => {
        if (!Array.isArray(sublist) || sublist.length === 0) {
          return null;
        }
        const sortedList = sublist.slice().sort((a, b) => a - b);
        const midIndex = Math.floor(sortedList.length / 2);

        if (sortedList.length % 2 === 0) {
          return (sortedList[midIndex - 1] + sortedList[midIndex]) / 2;
        } else {
          return sortedList[midIndex];
        }
      });
    };

    const legendChange = (data: any) => {
      if (data && data.selected) {
        const selectedData = data.selected;
        let newTooltipContent = '';

        if (selectedData['平均-3σ']) {
          newTooltipContent += '<br/>' + t('平均-3σ') + ':' + parseSize(averageMinus3SD);
        }

        if (selectedData['平均+3σ']) {
          newTooltipContent += '<br/>' + t('平均+3σ') + ':' + parseSize(averagePlus3SD);
        }

        if (selectedData['平均']) {
          newTooltipContent += '<br/>' + t('平均') + ':' + parseSize(averageUpperDistance);
        }

        if (selectedData['中央']) {
          newTooltipContent += '<br/>' + t('中央') + ':' + parseSize(medianUpperDistance);
        }
        setTooltipContent(newTooltipContent);
        setLegendSelected(selectedData);
      }
    };

    return (
      <div>
        <LFCBaseCharts
          option={{
            title: {
              text: title
            },
            legend: {
              id: 1,
              type: 'scroll',
              bottom: 0,
              data:
                searchType === SearchType.WORK
                  ? [
                      {name: t('最小値'), icon: 'circle'},
                      {name: t('上限'), icon: 'circle'},
                      {name: t('下限'), icon: 'circle'}
                    ]
                  : [
                      {name: t('箱ひげ図'), icon: 'circle'},
                      {name: t('群平均'), icon: 'circle'},
                      {name: t('群中央'), icon: 'circle'},
                      {name: t('上限'), icon: 'circle'},
                      {name: t('下限'), icon: 'circle'},
                      {name: t('中央'), icon: 'circle'},
                      {name: t('平均'), icon: 'circle'},
                      {name: t('平均+3σ'), icon: 'circle'},
                      {name: t('平均-3σ'), icon: 'circle'}
                    ],
              selected: legendSelected
            },
            tooltip: {
              trigger: 'axis', // 軸に沿ってtooltipを表示するために'axis'を使用
              formatter: function (params: any) {
                if (!Array.isArray(params) || params.length === 0) {
                  return 'データなし';
                }

                const getValue = (seriesName: string, index?: number) => {
                  const matchrow = params.find(p => p.seriesName === seriesName);
                  if (!matchrow) {
                    return '-';
                  }
                  if (!index) {
                    return parseSize(matchrow.value);
                  }
                  return parseSize(matchrow.value[index]);
                };

                if (searchType === SearchType.WORK) {
                  return `${params[0]?.name}<br>
                  <br>
                  <span style="font-weight:bold">${t('検査日時')}</span>: ${dayjs(
                    source.detail[params[0].dataIndex].lq_time
                      ? dayjs(source.detail[params[0].dataIndex].lq_time).format(
                          'YYYY-MM-DD HH:mm:ss'
                        )
                      : 'データなし'
                  ).format('YYYY-MM-DD HH:mm:ss')}<br/>
                  <span style="font-weight:bold">${t('シリアルナンバー')}</span>: ${
                    source.detail[params[0].dataIndex].serial ||
                    source.detail[params[0].dataIndex].uuid ||
                    'データなし'
                  }<br/>
                  <span style="font-weight:bold">${t('最小値')}</span>: ${getValue(
                    t('最小値')
                  )}<br/>
                  <span style="font-weight:bold">${t('上限')}</span>: ${getValue(t('上限'))}<br/>
                  <span style="font-weight:bold">${t('下限')}</span>: ${getValue(t('下限'))}<br/>
                  `;
                }

                return `${params[0]?.name}<br>
                <br>
                <span style="font-weight:bold">${t('最大')}</span>: ${getValue(
                  t('箱ひげ図'),
                  5
                )}<br/>
                <span style="font-weight:bold">${'Q3'}</span>: ${getValue(t('箱ひげ図'), 4)}<br/>
                <span style="font-weight:bold">${t('中央')}</span>: ${getValue(
                  t('箱ひげ図'),
                  3
                )}<br/>
                <span style="font-weight:bold">${'Q1'}</span>: ${getValue(t('箱ひげ図'), 2)}<br/>
                <span style="font-weight:bold">${t('最小')}</span>: ${getValue(
                  t('箱ひげ図'),
                  1
                )}<br/>
                <br/>
                <span style="font-weight:bold">${t('群平均')}</span>: ${getValue(t('群平均'))}<br/>
                <span style="font-weight:bold">${t('群中央')}</span>: ${getValue(t('群中央'))}<br/>
                <span style="font-weight:bold">${t('上限')}</span>: ${getValue(t('上限'))}<br/>
                <span style="font-weight:bold">${t('下限')}</span>: ${getValue(t('下限'))}<br/>
                ${tooltipContent}
                `;
              }
            },
            dataZoom: [
              {
                type: 'inside'
              }
            ],
            grid: {
              top: 40,
              left: 50,
              right: 50,
              bottom: 60
            },
            xAxis: {
              type: 'category',
              data: source.xlist,
              axisLabel: {
                rotate: '15',
                fontSize: 10
              }
            },
            yAxis: {
              type: 'value',
              min: (value: {min: number; max: number}) => {
                let val = value.min;
                if (legendSelected['平均-3σ']) {
                  val = Math.min(val, averageMinus3SD);
                }
                return Math.floor(val * 10) / 10;
              },
              max: (value: {min: number; max: number}) => {
                let val = value.max;
                if (legendSelected['平均+3σ']) {
                  val = Math.max(val, averagePlus3SD);
                }
                return Math.ceil(val * 10) / 10;
              }
            },
            series:
              searchType === SearchType.WORK
                ? [
                    {
                      name: t('最小値'),
                      type: 'line',
                      data: source.data_lower_distance,
                      color: blue[500]
                    },
                    {
                      name: t('上限'),
                      type: 'line',
                      color: pink[500],
                      data: source.data_upper_limits,
                      symbolSize: 0,
                      lineStyle: {
                        type: 'dotted'
                      },
                      connectNulls: true // null を無視して線をつなぐ
                    },
                    {
                      name: t('下限'),
                      type: 'line',
                      color: lightBlue[500],
                      data: source.data_lower_limits,
                      symbolSize: 0,
                      lineStyle: {
                        type: 'dotted'
                      },
                      connectNulls: true // null を無視して線をつなぐ
                    }
                  ]
                : [
                    {
                      name: t('箱ひげ図'),
                      type: 'boxplot',
                      data: boxData[1],
                      color: blue[500]
                    },
                    {
                      name: t('上限'),
                      type: 'line',
                      color: pink[500],
                      symbolSize: 0,
                      lineStyle: {
                        type: 'dotted'
                      },
                      data: source.data_upper_limits.map((e: any) => {
                        return e?.[0] ?? null;
                      }),
                      connectNulls: true // null を無視して線をつなぐ
                    },
                    {
                      name: t('下限'),
                      type: 'line',
                      color: lightBlue[500],
                      symbolSize: 0,
                      lineStyle: {
                        type: 'dotted'
                      },
                      data: source.data_lower_limits.map((e: any) => {
                        return e?.[0] ?? null;
                      }),
                      connectNulls: true // null を無視して線をつなぐ
                    },
                    {
                      name: t('中央'),
                      type: 'line',
                      color: colorPallet[2],
                      markLine: {
                        data: [
                          {
                            type: 'guide',
                            yAxis: medianUpperDistance,
                            name: t('中央')
                          }
                        ],
                        lineStyle: {
                          color: colorPallet[2],
                          type: 'solid',
                          width: 2
                        },
                        symbol: ['none', 'none'],
                        label: {
                          color: colorPallet[2],
                          fontSize: 10,
                          formatter: t('中央'),
                          show: true,
                          position: 'end'
                        }
                      }
                    },
                    {
                      name: t('平均'),
                      type: 'line',
                      color: colorPallet[4],
                      markLine: {
                        data: [
                          {
                            type: 'guide',
                            yAxis: averageUpperDistance,
                            name: t('平均')
                          }
                        ],
                        lineStyle: {
                          color: colorPallet[4],
                          type: 'solid',
                          width: 2
                        },
                        symbol: ['none', 'none'],
                        label: {
                          color: colorPallet[4],
                          fontSize: 10,
                          formatter: t('平均'),
                          show: true,
                          position: 'end'
                        }
                      }
                    },
                    {
                      name: t('平均+3σ'),
                      type: 'line',
                      color: colorPallet[5],
                      markLine: {
                        data: [
                          {
                            type: 'guide',
                            yAxis: averagePlus3SD,
                            name: t('平均+3σ')
                          }
                        ],
                        lineStyle: {
                          color: colorPallet[5],
                          type: 'solid',
                          width: 2
                        },
                        symbol: ['none', 'none'],
                        label: {
                          color: colorPallet[5],
                          fontSize: 10,
                          formatter: t('平均+3σ'),
                          show: true,
                          position: 'end'
                        }
                      }
                    },
                    {
                      name: t('平均-3σ'),
                      type: 'line',
                      color: colorPallet[6],
                      markLine: {
                        data: [
                          {
                            type: 'guide',
                            yAxis: averageMinus3SD,
                            name: t('平均-3σ')
                          }
                        ],
                        lineStyle: {
                          color: colorPallet[6],
                          type: 'solid',
                          width: 2
                        },
                        symbol: ['none', 'none'],
                        label: {
                          color: colorPallet[6],
                          fontSize: 10,
                          formatter: t('平均-3σ'),
                          show: true,
                          position: 'end'
                        }
                      }
                    },
                    {
                      name: t('群平均'),
                      type: 'line',
                      legendHoverLink: true,
                      showSymbol: true,
                      showAllSymbol: true,
                      data: source ? calcDataAvgMel(source) : false,
                      lineStyle: {
                        width: 1
                      },
                      color: colorPallet[1],
                      connectNulls: true // データがない部分を飛ばして線を繋ぐ
                    },
                    {
                      name: t('群中央'),
                      type: 'line',
                      legendHoverLink: true,
                      showSymbol: true,
                      showAllSymbol: true,
                      data: source ? calcDataMiddleMel(source) : [],
                      lineStyle: {
                        width: 1
                      },
                      color: colorPallet[3],
                      connectNulls: true // データがない部分を飛ばして線を繋ぐ
                    }
                  ]
          }}
          exportData={exportData}
          exportFields={exportFields}
          exportFilename={`CloudDistance_${t('最小値推移')}`}
          height={height ? height : '100px'}
          onEvents={{
            legendselectchanged: (params: any) => {
              legendChange(params);
            }
          }}
          zoomCharts={zoomCharts ? zoomCharts : undefined}
          zoomChartsClose={zoomChartsClose ? zoomChartsClose : undefined}
          zoomChartId={zoomChartId ? zoomChartId : undefined}
          zoomStatus={zoomChartId === zoomedChartId}
        />
      </div>
    );
  }
);

LFCChartsCloudDistanceMinTrend.displayName = 'LFCChartsCloudDistanceMinTrend';

export default LFCChartsCloudDistanceMinTrend;
